/**
 * 配置管理工具类
 * 提供配置数据的加密、解密、缓存管理功能
 */

import { configApi } from '@/api/api'

class ConfigManager {
  constructor() {
    this.cacheKey = 'app_config_cache'
    this.cacheTimeKey = 'app_config_cache_time'
    this.cacheExpireTime = 24 * 60 * 60 * 1000 // 24小时过期
  }

  /**
   * Base64 编码
   * @param {string} str - 要编码的字符串
   * @returns {string} - 编码后的字符串
   */
  base64Encode(str) {
    try {
      return btoa(unescape(encodeURIComponent(str)))
    } catch (error) {
      console.error('Base64编码失败:', error)
      return str
    }
  }

  /**
   * Base64 解码
   * @param {string} str - 要解码的字符串
   * @returns {string} - 解码后的字符串
   */
  base64Decode(str) {
    try {
      return decodeURIComponent(escape(atob(str)))
    } catch (error) {
      console.error('Base64解码失败:', error)
      return str
    }
  }

  /**
   * 检查缓存是否过期
   * @returns {boolean} - true表示过期，false表示未过期
   */
  isCacheExpired() {
    const cacheTime = localStorage.getItem(this.cacheTimeKey)
    if (!cacheTime) return true
    
    const now = Date.now()
    const cacheTimestamp = parseInt(cacheTime)
    return (now - cacheTimestamp) > this.cacheExpireTime
  }

  /**
   * 从本地缓存获取配置
   * @returns {Object|null} - 配置对象或null
   */
  getConfigFromCache() {
    try {
      if (this.isCacheExpired()) {
        this.clearCache()
        return null
      }
      
      const cachedData = localStorage.getItem(this.cacheKey)
      if (!cachedData) return null
      
      const decodedData = this.base64Decode(cachedData)
      return JSON.parse(decodedData)
    } catch (error) {
      console.error('从缓存获取配置失败:', error)
      this.clearCache()
      return null
    }
  }

  /**
   * 保存配置到本地缓存
   * @param {Object} config - 配置对象
   */
  saveConfigToCache(config) {
    try {
      const configStr = JSON.stringify(config)
      const encodedData = this.base64Encode(configStr)
      
      localStorage.setItem(this.cacheKey, encodedData)
      localStorage.setItem(this.cacheTimeKey, Date.now().toString())
      
      console.log('配置已保存到缓存')
    } catch (error) {
      console.error('保存配置到缓存失败:', error)
    }
  }

  /**
   * 清除缓存
   */
  clearCache() {
    localStorage.removeItem(this.cacheKey)
    localStorage.removeItem(this.cacheTimeKey)
    console.log('配置缓存已清除')
  }

  /**
   * 从服务器获取配置并缓存
   * @param {boolean} forceRefresh - 是否强制刷新，忽略缓存
   * @returns {Promise<Object>} - 配置对象
   */
  async getConfig(forceRefresh = false) {
    try {
      // 如果不强制刷新，先尝试从缓存获取
      if (!forceRefresh) {
        const cachedConfig = this.getConfigFromCache()
        if (cachedConfig) {
          console.log('从缓存获取配置成功')
          return cachedConfig
        }
      }

      // 从服务器获取配置
      console.log('从服务器获取配置...')
      const response = await configApi.getConfig()
      
      if (response && response.data) {
        const configData = response.data
        // configData.adjustmentValues.settings.enableAdjustments=false
        // 解析configValue字符串为JSON对象
        const parsedConfig = JSON.parse(configData.configValue)
        parsedConfig.adjustmentValues.settings.enableAdjustments=false
        // 保存到缓存
        this.saveConfigToCache(parsedConfig)
        
        console.log('从服务器获取配置成功')
        return parsedConfig
      } else {
        throw new Error('服务器返回数据格式错误')
      }
    } catch (error) {
      console.error('获取配置失败:', error)
      
      // 如果服务器请求失败，尝试从缓存获取（即使可能过期）
      const cachedConfig = this.getConfigFromCache()
      if (cachedConfig) {
        console.warn('服务器请求失败，使用缓存配置')
        return cachedConfig
      }
      
      // 返回默认配置
      return this.getDefaultConfig()
    }
  }

  /**
   * 保存配置到服务器并更新缓存
   * @param {Object} config - 配置对象
   * @returns {Promise<boolean>} - 保存是否成功
   */
  async saveConfig(config) {
    try {
      const configJson = JSON.stringify(config)
      const response = await configApi.saveConfig(configJson)
      
      if (response && response.success) {
        // 保存成功后更新缓存
        this.saveConfigToCache(config)
        console.log('配置保存成功')
        return true
      } else {
        throw new Error('服务器保存失败')
      }
    } catch (error) {
      console.error('保存配置失败:', error)
      return false
    }
  }

  /**
   * 获取默认配置
   * @returns {Object} - 默认配置对象
   */
  getDefaultConfig() {
    return {
      adjustmentValues: {
        inventory: {
          description: "库存相关调整值",
          totalAmount: 0
        },
        receivable: {
          description: "应收账款相关调整值",
          totalReceivable: 0
        },
        bankAccount: {
          description: "银行账户相关调整值",
          totalBalance: 0,
          totalIncome: 0,
          totalAccountCount: 0,
          netInflow: 0
        }
      },
      settings: {
        enableAdjustments: false,
        lastModified: new Date().toISOString(),
        version: "1.0.0"
      }
    }
  }

  /**
   * 获取特定模块的调整值
   * @param {string} module - 模块名称 (inventory, receivable, bankAccount)
   * @returns {Promise<Object>} - 调整值对象
   */
  async getAdjustmentValues(module) {
    try {
      const config = await this.getConfig()
      return config.adjustmentValues?.[module] || {}
    } catch (error) {
      console.error(`获取${module}调整值失败:`, error)
      return {}
    }
  }

  /**
   * 检查是否启用调整功能
   * @returns {Promise<boolean>} - 是否启用
   */
  async isAdjustmentEnabled() {
    // try {
    //   const config = await this.getConfig()
    //   return config.settings?.enableAdjustments || false
    // } catch (error) {
    //   console.error('检查调整功能状态失败:', error)
    //   return false
    // }
    return false;
  }
}

// 创建单例实例
const configManager = new ConfigManager()

export default configManager